<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { localUserStore } from "../../Connection/LocalUserStore";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { inputFormFocusStore } from "../../Stores/UserInputStore";
    import { toastStore } from "../../Stores/ToastStoreSingleton";
    import MercadologicaPostItToast from "./MercadologicaPostItToast.svelte";
    import MemberAutocomplete from "../Input/MemberAutocomplete.svelte";
    import {
        createMercadologicaPostIt,
        deleteMercadologicaPostIt,
        deleteMercadologicaSentPostIt,
        fetchMercadologicaPostIts,
        markMercadologicaPostItAsRead,
        mercadologicaPostItStore,
        type MercadologicaPostIt,
    } from "../../Stores/MercadologicaPostItStore";

    const POLLING_INTERVAL_MS = 10_000;
    const MAX_MESSAGE_LENGTH = 300;

    let message = $state("");
    let postIts: MercadologicaPostIt[] = $state([]);
    let sentPostIts: MercadologicaPostIt[] = $state([]);
    let activeTab: "received" | "sent" = $state("received");
    let loading = $state(false);
    let sending = $state(false);
    let errorMessage = $state("");
    let successMessage = $state("");
    let pollingTimer: number | undefined;
    let confirmDeleteId = $state<string | undefined>();
    let busyPostItId = $state<string | undefined>();
    let previousMode: "closed" | "compose" | "inbox" = "closed";
    let hasLoadedPostIts = false;
    let knownUnreadPostItIds = new Set<string>();
    let selectingRecipient = $state(false);

    let remainingCharacters = $derived(MAX_MESSAGE_LENGTH - message.length);
    let visiblePostIts = $derived(activeTab === "received" ? postIts : sentPostIts);

    function openRecipientPicker(): void {
        selectingRecipient = true;
        errorMessage = "";
        successMessage = "";
        inputFormFocusStore.set(true);
    }

    function cancelRecipientPicker(): void {
        selectingRecipient = false;
        errorMessage = "";
        inputFormFocusStore.set(false);
    }

    function selectRecipient(selectedUser: {
        index: number;
        label: string;
        value: string;
    }): void {
        const ownUuid =
            localUserStore.getLocalUser()?.uuid
                ?.trim()
                .toLocaleLowerCase();

        const selectedUuid =
            selectedUser.value.trim().toLocaleLowerCase();

        if (ownUuid && selectedUuid === ownUuid) {
            errorMessage =
                "Você não pode deixar um recado para si mesmo.";
            return;
        }

        const cleanName =
            selectedUser.label
                .replace(/\s+\([^)]*\)\s*$/, "")
                .trim() || selectedUser.value;

        selectingRecipient = false;
        errorMessage = "";
        inputFormFocusStore.set(false);

        mercadologicaPostItStore.openCompose({
            uuid: selectedUser.value,
            name: cleanName,
        });
    }


    async function loadPostIts(showLoading = true): Promise<void> {
        if (!localUserStore.isLogged()) {
            return;
        }

        if (showLoading) {
            loading = true;
        }

        try {
            const collection = await fetchMercadologicaPostIts();
            const unreadPostIts = collection.received.filter((postIt) => postIt.readAt === null);

            // Se for o primeiro carregamento após o login,
            // todos os recados não lidos são tratados como novos.
            // Assim, recados deixados enquanto o usuário estava offline
            // também geram toast e som quando ele entra.
            const newPostIts = hasLoadedPostIts
                ? unreadPostIts.filter(
                      (postIt) => !knownUnreadPostItIds.has(postIt.id),
                  )
                : unreadPostIts;

            for (const postIt of newPostIts) {
                toastStore.addToast(
                    MercadologicaPostItToast,
                    {
                        senderName: postIt.senderName,
                        message: postIt.message,
                        duration: 10000,
                    },
                    `mercadologica-post-it-${postIt.id}`,
                );
            }

            if (newPostIts.length > 0) {
                try {
                    gameManager.getCurrentGameScene().playSound("icq-uh-oh", 0.35);
                } catch (soundError) {
                    console.error("Failed to play post-it notification sound:", soundError);
                }
            }

            knownUnreadPostItIds = new Set(unreadPostIts.map((postIt) => postIt.id));
            hasLoadedPostIts = true;

            postIts = collection.received;
            sentPostIts = collection.sent;
            errorMessage = "";
        } catch (error) {
            if (showLoading) {
                errorMessage = error instanceof Error ? error.message : "Não foi possível carregar os recados.";
            }
        } finally {
            if (showLoading) {
                loading = false;
            }
        }
    }

    async function sendPostIt(): Promise<void> {
        const target = $mercadologicaPostItStore.target;
        const cleanMessage = message.trim();

        if (!target || !cleanMessage || sending) {
            return;
        }

        sending = true;
        errorMessage = "";
        successMessage = "";

        try {
            await createMercadologicaPostIt(target, cleanMessage);
            message = "";
            successMessage = `Recado deixado para ${target.name}.`;

            window.setTimeout(() => {
                mercadologicaPostItStore.close();
                successMessage = "";
            }, 900);
        } catch (error) {
            errorMessage = error instanceof Error ? error.message : "Não foi possível enviar o recado.";
        } finally {
            sending = false;
        }
    }

    async function markAsRead(postIt: MercadologicaPostIt): Promise<void> {
        if (postIt.readAt !== null || busyPostItId !== undefined) {
            return;
        }

        const previousPostIts = postIts;
        const optimisticReadAt = Date.now();

        busyPostItId = postIt.id;
        errorMessage = "";

        // Atualização visual imediata.
        postIts = postIts.map((item) =>
            item.id === postIt.id ? { ...item, readAt: optimisticReadAt } : item,
        );
        mercadologicaPostItStore.setUnreadCount(postIts.filter((item) => item.readAt === null).length);

        try {
            const updated = await markMercadologicaPostItAsRead(postIt.id);
            postIts = postIts.map((item) => (item.id === updated.id ? updated : item));
            knownUnreadPostItIds.delete(postIt.id);
        } catch (error) {
            // Desfaz a alteração visual caso o servidor recuse.
            postIts = previousPostIts;
            mercadologicaPostItStore.setUnreadCount(postIts.filter((item) => item.readAt === null).length);
            errorMessage = error instanceof Error ? error.message : "Não foi possível marcar o recado como lido.";
        } finally {
            busyPostItId = undefined;
        }
    }

    async function removePostIt(postIt: MercadologicaPostIt): Promise<void> {
        if (busyPostItId !== undefined) {
            return;
        }

        // Primeiro clique pede confirmação dentro do próprio botão.
        if (confirmDeleteId !== postIt.id) {
            confirmDeleteId = postIt.id;

            window.setTimeout(() => {
                if (confirmDeleteId === postIt.id) {
                    confirmDeleteId = undefined;
                }
            }, 5000);

            return;
        }

        const deletingFromSent = activeTab === "sent";
        const previousPostIts = postIts;
        const previousSentPostIts = sentPostIts;

        busyPostItId = postIt.id;
        confirmDeleteId = undefined;
        errorMessage = "";

        // Remove imediatamente da aba correta.
        if (deletingFromSent) {
            sentPostIts = sentPostIts.filter((item) => item.id !== postIt.id);
        } else {
            postIts = postIts.filter((item) => item.id !== postIt.id);
            mercadologicaPostItStore.setUnreadCount(postIts.filter((item) => item.readAt === null).length);
        }

        try {
            if (deletingFromSent) {
                await deleteMercadologicaSentPostIt(postIt.id);
            } else {
                await deleteMercadologicaPostIt(postIt.id);
                knownUnreadPostItIds.delete(postIt.id);
            }
        } catch (error) {
            // Recoloca na aba correta caso o servidor recuse.
            postIts = previousPostIts;
            sentPostIts = previousSentPostIts;
            mercadologicaPostItStore.setUnreadCount(postIts.filter((item) => item.readAt === null).length);
            errorMessage = error instanceof Error ? error.message : "Não foi possível excluir o recado.";
        } finally {
            busyPostItId = undefined;
        }
    }

    function close(): void {
        message = "";
        errorMessage = "";
        successMessage = "";
        selectingRecipient = false;
        inputFormFocusStore.set(false);
        mercadologicaPostItStore.close();
    }

    function formatDate(timestamp: number): string {
        return new Intl.DateTimeFormat("pt-BR", {
            dateStyle: "short",
            timeStyle: "short",
        }).format(new Date(timestamp));
    }

    function handleWindowKeydown(event: KeyboardEvent): void {
        if (event.key === "Escape" && $mercadologicaPostItStore.mode !== "closed") {
            close();
        }
    }

    $effect(() => {
        const currentMode = $mercadologicaPostItStore.mode;

        // Só executa ao mudar realmente de janela.
        // Atualizações do contador não podem apagar o texto digitado.
        if (currentMode === previousMode) {
            return;
        }

        previousMode = currentMode;
        confirmDeleteId = undefined;

        if (currentMode === "inbox") {
            activeTab = "received";
            void loadPostIts();
        }

        if (currentMode === "compose") {
            message = "";
            errorMessage = "";
            successMessage = "";
        }
    });

    onMount(() => {
        if (localUserStore.isLogged()) {
            void loadPostIts(false);
            pollingTimer = window.setInterval(() => {
                // Nunca atualiza enquanto alguém está escrevendo.
                if (
                    $mercadologicaPostItStore.mode !== "compose" &&
                    !selectingRecipient &&
                    busyPostItId === undefined
                ) {
                    void loadPostIts(false);
                }
            }, POLLING_INTERVAL_MS);
        }
    });

    onDestroy(() => {
        if (pollingTimer !== undefined) {
            window.clearInterval(pollingTimer);
        }
        inputFormFocusStore.set(false);
    });
</script>

<svelte:window onkeydown={handleWindowKeydown} />

{#if $mercadologicaPostItStore.mode !== "closed"}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
        class="fixed inset-0 z-[600] flex items-center justify-center bg-black/55 p-4 pointer-events-auto"
        role="presentation"
        onclick={(event) => {
            if (event.target === event.currentTarget) {
                close();
            }
        }}
    >
        <section
            class="relative w-full max-w-lg overflow-hidden rounded-2xl border border-yellow-200/50 bg-[#fff3a6] text-slate-900 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={$mercadologicaPostItStore.mode === "compose" ? "Deixar recado" : "Meus recados"}
        >
            <div
                class="absolute right-7 top-0 h-10 w-20 -translate-y-3 rotate-2 bg-white/40 shadow-sm"
                aria-hidden="true"
            ></div>

            <header class="relative flex items-center justify-between border-b border-amber-800/15 px-5 py-4">
                <div>
                    <h2 class="m-0 text-xl font-bold">
                        {$mercadologicaPostItStore.mode === "compose" ? "📝 Deixar recado" : "📌 Meus recados"}
                    </h2>

                    {#if $mercadologicaPostItStore.mode === "compose" && $mercadologicaPostItStore.target}
                        <p class="m-0 mt-1 text-sm text-slate-700">
                            Para: <strong>{$mercadologicaPostItStore.target.name}</strong>
                        </p>
                    {:else}
                        <p class="m-0 mt-1 text-sm text-slate-700">
                            {$mercadologicaPostItStore.unreadCount}
                            {$mercadologicaPostItStore.unreadCount === 1 ? "recado não lido" : "recados não lidos"}
                        </p>
                    {/if}
                </div>

                <button
                    type="button"
                    class="relative z-10 flex h-9 w-9 items-center justify-center rounded-full bg-slate-900/10 text-xl hover:bg-slate-900/20"
                    title="Fechar"
                    onclick={close}
                >
                    ×
                </button>
            </header>

            {#if $mercadologicaPostItStore.mode === "compose"}
                <div class="p-5">
                    <label for="mercadologica-post-it-message" class="mb-2 block text-sm font-bold">
                        Escreva seu recado
                    </label>

                    <textarea
                        id="mercadologica-post-it-message"
                        class="h-40 w-full resize-none rounded-xl border border-amber-800/20 bg-yellow-50/80 p-4 text-base text-slate-900 outline-none placeholder:text-slate-500 focus:border-amber-700"
                        maxlength={MAX_MESSAGE_LENGTH}
                        placeholder="Digite uma mensagem curta..."
                        bind:value={message}
                        onfocus={() => inputFormFocusStore.set(true)}
                        onblur={() => inputFormFocusStore.set(false)}
                    ></textarea>

                    <div class="mt-1 text-right text-xs text-slate-600">
                        {remainingCharacters} caracteres restantes
                    </div>

                    {#if errorMessage}
                        <div class="mt-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{errorMessage}</div>
                    {/if}

                    {#if successMessage}
                        <div class="mt-3 rounded-lg bg-green-100 px-3 py-2 text-sm text-green-800">
                            {successMessage}
                        </div>
                    {/if}

                    <div class="mt-5 flex justify-end gap-3">
                        <button
                            type="button"
                            class="rounded-lg bg-slate-900/10 px-4 py-2 font-bold hover:bg-slate-900/20"
                            onclick={close}
                        >
                            Cancelar
                        </button>

                        <button
                            type="button"
                            class="rounded-lg bg-amber-700 px-5 py-2 font-bold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={!message.trim() || sending}
                            onclick={() => void sendPostIt()}
                        >
                            {sending ? "Enviando..." : "Deixar recado"}
                        </button>
                    </div>
                </div>
            {:else}
                <div class="border-b border-amber-800/15 p-3">
                    <button
                        type="button"
                        class="w-full rounded-lg bg-amber-700 px-4 py-2 font-bold text-white hover:bg-amber-800"
                        onclick={() => {
                            if (selectingRecipient) {
                                cancelRecipientPicker();
                            } else {
                                openRecipientPicker();
                            }
                        }}
                    >
                        {selectingRecipient
                            ? "Cancelar novo recado"
                            : "✍️ Novo recado"}
                    </button>

                    {#if selectingRecipient}
                        <div class="mt-3 rounded-xl border border-amber-800/15 bg-white/45 p-3">
                            <label
                                for="mercadologica-post-it-recipient"
                                class="mb-2 block text-sm font-bold text-slate-800"
                            >
                                Para quem você quer deixar o recado?
                            </label>

                            <MemberAutocomplete
                                placeholder="Digite o nome ou e-mail..."
                                select={selectRecipient}
                            />

                            {#if errorMessage}
                                <div class="mt-2 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">
                                    {errorMessage}
                                </div>
                            {/if}

                            <p class="m-0 mt-2 text-xs text-slate-600">
                                Usuários offline também aparecem nesta busca.
                            </p>
                        </div>
                    {/if}
                </div>

                <div class="grid grid-cols-2 gap-2 border-b border-amber-800/15 p-3">
                    <button
                        type="button"
                        class={`rounded-lg px-3 py-2 text-sm font-bold transition ${
                            activeTab === "received"
                                ? "bg-amber-700 text-white"
                                : "bg-slate-900/10 text-slate-800 hover:bg-slate-900/20"
                        }`}
                        onclick={() => {
                            activeTab = "received";
                            confirmDeleteId = undefined;
                            errorMessage = "";
                        }}
                    >
                        Recebidos
                        {#if $mercadologicaPostItStore.unreadCount > 0}
                            ({$mercadologicaPostItStore.unreadCount})
                        {/if}
                    </button>

                    <button
                        type="button"
                        class={`rounded-lg px-3 py-2 text-sm font-bold transition ${
                            activeTab === "sent"
                                ? "bg-amber-700 text-white"
                                : "bg-slate-900/10 text-slate-800 hover:bg-slate-900/20"
                        }`}
                        onclick={() => {
                            activeTab = "sent";
                            confirmDeleteId = undefined;
                            errorMessage = "";
                        }}
                    >
                        Enviados
                    </button>
                </div>

                <div class="max-h-[58vh] overflow-y-auto p-4">
                    {#if loading}
                        <div class="py-10 text-center text-slate-700">Carregando recados...</div>
                    {:else if errorMessage}
                        <div class="rounded-lg bg-red-100 px-3 py-3 text-sm text-red-800">{errorMessage}</div>
                    {:else if visiblePostIts.length === 0}
                        <div class="py-12 text-center">
                            <div class="mb-3 text-5xl">{activeTab === "received" ? "📭" : "📤"}</div>
                            <p class="m-0 font-bold">
                                {activeTab === "received"
                                    ? "Você ainda não recebeu recados."
                                    : "Você ainda não enviou recados."}
                            </p>
                        </div>
                    {:else}
                        <div class="flex flex-col gap-3">
                            {#each visiblePostIts as postIt (postIt.id)}
                                <article
                                    class={`rounded-xl border p-4 shadow-sm ${
                                        activeTab === "received" && postIt.readAt === null
                                            ? "border-amber-500 bg-yellow-50"
                                            : "border-amber-900/10 bg-white/55"
                                    }`}
                                >
                                    <div class="flex items-start justify-between gap-3">
                                        <div>
                                            <div class="font-bold">
                                                {#if activeTab === "received"}
                                                    {postIt.readAt === null ? "📌 " : ""}{postIt.senderName}
                                                {:else}
                                                    Para: {postIt.recipientName}
                                                {/if}
                                            </div>
                                            <div class="mt-1 text-xs text-slate-600">
                                                Enviado em {formatDate(postIt.createdAt)}
                                            </div>
                                        </div>

                                        {#if activeTab === "received" && postIt.readAt === null}
                                            <span class="rounded-full bg-amber-600 px-2 py-1 text-[11px] font-bold text-white">
                                                Novo
                                            </span>
                                        {:else if activeTab === "sent" && postIt.readAt === null}
                                            <span class="rounded-full bg-slate-600 px-2 py-1 text-[11px] font-bold text-white">
                                                Não lido
                                            </span>
                                        {:else if activeTab === "sent" && postIt.readAt !== null}
                                            <span class="rounded-full bg-green-700 px-2 py-1 text-[11px] font-bold text-white">
                                                Lido
                                            </span>
                                        {/if}
                                    </div>

                                    <p class="my-4 whitespace-pre-wrap break-words text-[15px] leading-6">
                                        {postIt.message}
                                    </p>

                                    {#if activeTab === "sent"}
                                        <div
                                            class={`rounded-lg px-3 py-2 text-sm font-bold ${
                                                postIt.readAt === null
                                                    ? "bg-slate-900/10 text-slate-700"
                                                    : "bg-green-100 text-green-800"
                                            }`}
                                        >
                                            {postIt.readAt === null
                                                ? "Aguardando leitura"
                                                : `Lido em ${formatDate(postIt.readAt)}`}
                                        </div>
                                    {/if}

                                    <div class="mt-3 flex justify-end gap-2">
                                        {#if activeTab === "received" && postIt.readAt === null}
                                            <button
                                                type="button"
                                                class="rounded-lg bg-slate-900/10 px-3 py-2 text-sm font-bold hover:bg-slate-900/20"
                                                disabled={busyPostItId !== undefined}
                                                onclick={(event) => {
                                                    event.stopPropagation();
                                                    void markAsRead(postIt);
                                                }}
                                            >
                                                {busyPostItId === postIt.id ? "Marcando..." : "Marcar como lido"}
                                            </button>
                                        {/if}

                                        <button
                                            type="button"
                                            class={`rounded-lg px-3 py-2 text-sm font-bold text-white disabled:opacity-50 ${
                                                confirmDeleteId === postIt.id
                                                    ? "bg-amber-700 hover:bg-amber-800"
                                                    : "bg-red-700 hover:bg-red-800"
                                            }`}
                                            disabled={busyPostItId !== undefined}
                                            onclick={(event) => {
                                                event.stopPropagation();
                                                void removePostIt(postIt);
                                            }}
                                        >
                                            {busyPostItId === postIt.id
                                                ? "Excluindo..."
                                                : confirmDeleteId === postIt.id
                                                  ? "Clique novamente para excluir"
                                                  : "Excluir"}
                                        </button>
                                    </div>
                                </article>
                            {/each}
                        </div>
                    {/if}
                </div>
            {/if}
        </section>
    </div>
{/if}
